import React, { useState, useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import View from './view';
import RouteUtils from 'services/RouteUtils';
import { v1 as uuidV1 } from 'uuid';
import DocumentUtils from 'services/DocumentUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import useLockDocument from 'hooks/useLockDocument';
import { IsAdmin, IsPhysician, IsTypePhlebotomist } from 'careand-redux/selectors';
import PerformanceUtils from 'services/PerformanceUtils';
import moment from 'moment-timezone';
import {
  useSpotlightDocumentContext,
  useSpotlightPatientContext,
} from 'services/Spotlight/SpotlightAIContext';

interface DocumentDetailsProps {
  document: any;
  patient?: any;
  onEdit?: (result: any) => void;
  disableApprove?: boolean;
  disableEdit?: boolean;
  disableSend?: boolean;
  disablePatientBadge?: boolean;
  approve?: boolean;
  onSave?: (document: any) => any;
  onCancel?: () => void;
  hideEditDocument?: boolean;
  allowExpand?: boolean;
  autoLoad?: boolean;
  allowDelete?: boolean;
  onClose?: () => void;
  isModal?: boolean;
  extraButtons?: React.ReactElement;
  onDocumentChange?: (value: any) => void;
  allowPin?: boolean;
  pinned?: boolean;
  allowLink?: boolean;
  previewElement?: React.ReactElement;
  fitWidth?: boolean;
  openCPP?: boolean;
  overridedPatient?: any;
  loading?: boolean;
  alwaysAllowSave?: boolean;
  hidePin?: boolean;
  startShowingDetails?: boolean;
  hidePrint?: boolean;
  style?: React.CSSProperties;
}

const DocumentDetails = (props: DocumentDetailsProps) => {
  let { document, openCPP } = props;

  const nav = useNavStack(Stacks.Master);
  const dispatch = useAppDispatch();

  const documentId = document.id;

  const [loading, setLoading] = useState(false);
  const [updatingPdf, setUpdatingPdf] = useState(false);
  const [value, setValue] = useState(PerformanceUtils.clone(document));
  const [linkModal, setLinkModal] = useState(false);
  const [documentTemplateModal, setDocumentTemplateModal] = useState(false);
  const [signModal, setSignModal] = useState(false);
  const [signModal2, setSignModal2] = useState(false);
  const [previewKey, setPreviewKey] = useState(uuidV1());
  const [showDocumentEdit, setShowDocumentEdit] = useState(false);
  const [chargingPatient, setChargingPatient] = useState(false);
  const autoAssignedPhysicianRef = useRef<string | undefined>(undefined);

  const physicianId = value.physician_id;

  const patients = useAppSelector((state) => state.patients.byId);
  const patient = patients[value.patient_id];
  const physician = useAppSelector((state) => state.physicians.byId[physicianId]);
  const primaryPhysician = useAppSelector(
    (state) => state.physicians.byId[patient?.primary_physician!],
  );
  const authentication = useAppSelector((state) => state.authentication);

  const isAdmin = useAppSelector(IsAdmin);
  const isPractitioner = useAppSelector(IsPhysician);
  const isPhlebotomist = useAppSelector(IsTypePhlebotomist);

  const hasDocumentChanged = !_.isEqual(value, document);

  const ownPhysician = isPractitioner ? authentication.user : undefined;
  const ownId = authentication.activeUserId;
  const isApproved = document.approved;

  const [lockedId, lockedName] = useLockDocument(documentId, !isApproved);

  useSpotlightPatientContext(value.patient_id);

  useSpotlightDocumentContext(undefined, value);

  useEffect(() => {
    const isUnread = document.unread_by_physician_id === ownId;
    if (isUnread) {
      const update = { ...document, unread_by_physician_id: undefined };
      dispatch(api.Documents.updateDocument({ patientDocument: update }));
    }
  }, [dispatch, document, ownId]);

  useEffect(() => {
    if (!!props.overridedPatient) {
      setValue((prev: any) => {
        if (prev.patient_id === props.overridedPatient.id) return prev;
        return { ...prev, patient_id: props.overridedPatient.id };
      });
    }
  }, [props.overridedPatient]);

  useEffect(() => {
    if (patient === undefined && value.patient_id) {
      dispatch(api.Patients.getPatient({ id: value.patient_id }));
    }
  }, [dispatch, value, patient]);

  useEffect(() => {
    if (!props.onDocumentChange) {
      saveDocument(document);
    }
  }, [document]);

  const allowAdminApprove = useMemo(() => {
    const ALLOWED_TYPES = [
      DocumentUtils.DOCUMENT_TYPE.demographics,
      DocumentUtils.DOCUMENT_TYPE.immunization_record,
      DocumentUtils.DOCUMENT_TYPE.appointment_notification,
      DocumentUtils.DOCUMENT_TYPE.historical_records,
    ];
    return (
      isAdmin &&
      !!value.patient_id &&
      ALLOWED_TYPES.includes(value.document_type) &&
      !value.approved
    );
  }, [isAdmin, value.document_type, value.patient_id, value.approved]);

  function openDocumentEdit(val: boolean) {
    setShowDocumentEdit(val);
    if (!val) changePreviewKey();
  }

  function changeSignModal(val: boolean) {
    setSignModal(val);
    if (!val) changePreviewKey();
  }

  function changeSignModal2(val: boolean) {
    setSignModal2(val);
    if (!val) changePreviewKey();
  }

  function changePreviewKey() {
    setPreviewKey(uuidV1());
  }

  function saveDocument(doc: any) {
    let val = PerformanceUtils.clone(doc);
    setValue(val);
    if (props.onDocumentChange) props.onDocumentChange(val);
  }

  function resetDocument() {
    if (props.onCancel) return props.onCancel();
    saveDocument(document);
  }

  async function approveDocument(hide: boolean) {
    let newValue = { ...value, hide, approved: true };
    saveDocument(newValue);
    await updateDocument(newValue);
  }

  function updateDocumentFields(data: Record<string, any>) {
    if (loading) return;
    let newDocument = { ...value, ...data };

    // A physician picked explicitly by staff is never treated as auto-assigned
    if ('physician_id' in data) autoAssignedPhysicianRef.current = undefined;

    // Faxed-in lab results (e.g. Dynacare rough copies) duplicate results the
    // provider already received electronically, so they shouldn't land in the
    // provider's pending queue via the primary-physician auto-fill.
    const isFaxedDiagnostic =
      DocumentUtils.isInboundFax(newDocument) &&
      newDocument.document_type === DocumentUtils.DOCUMENT_TYPE.diagnostic;

    if (
      value.patient_id !== newDocument.patient_id &&
      !newDocument.physician_id &&
      !isFaxedDiagnostic
    ) {
      let pat = patients[data.patient_id];
      if (pat && pat.primary_physician) {
        newDocument.physician_id = pat.primary_physician;
        autoAssignedPhysicianRef.current = pat.primary_physician;
      }
    } else if (
      isFaxedDiagnostic &&
      !!newDocument.physician_id &&
      newDocument.physician_id === autoAssignedPhysicianRef.current
    ) {
      newDocument.physician_id = undefined;
      autoAssignedPhysicianRef.current = undefined;
    }

    saveDocument(newDocument);
  }

  function handleDateChange(field: string, val: any) {
    updateDocumentFields({ [field]: val });
  }

  async function chargePatient() {
    setChargingPatient(true);
    await dispatch(
      api.Documents.payDocument({
        documentId: document.id,
        stringResource: { value: '' + value.fee },
      }),
      {
        title: 'Document Fee',
        successText: 'Document paid successfully!',
      },
    );
    setChargingPatient(false);
  }

  async function updateDocument(
    documentValue = value,
    showNotification = true,
    skipOnSave = false,
  ) {
    let doc = PerformanceUtils.clone(documentValue);

    if (!skipOnSave && props.onSave !== undefined) return props.onSave(doc);

    if (doc.fee) doc.fee = parseFloat(doc.fee);

    setLoading(true);
    saveDocument(doc);
    let result = await dispatch(
      api.Documents.updateDocument({ patientDocument: doc }),
      showNotification
        ? {
            errorTitle: 'Document',
          }
        : undefined,
    );
    setLoading(false);
    if (result && !skipOnSave) {
      if (props.onEdit) props.onEdit(result);
      return result;
    }
  }

  async function updateDocumentPDF(html: string, metadata: any, close = true) {
    metadata = metadata ? JSON.stringify(metadata) : metadata;

    setUpdatingPdf(true);

    let result = await dispatch(
      api.Documents.editDocumentFromHtml({
        documentId: document.id,
        hTMLData: {
          html,
          metadata,
        },
        patientId: document.patient_id,
      }),
      {
        errorTitle: 'Edit Document PDF',
      },
    );
    setUpdatingPdf(false);
    changePreviewKey();

    if (result) {
      if (close) setDocumentTemplateModal(false);
      saveDocument(result);
      return result;
    }
  }

  function onPhysicianClick() {
    if (physician) {
      let route = RouteUtils.getRoute(RouteUtils.routes().staff, {
        ':staffId': physician.id,
      });
      nav.push(route);
    }
  }

  async function saveComment(text: string, notification: boolean) {
    let originalComments = value.general_comments;
    let data = {
      author_id: ownId,
      text,
      date: moment().unix(),
      read_by: [ownId],
      notifications_disabled: !notification,
    };
    let general_comments = [...(originalComments || []), data];
    await updateDocument({ ...value, general_comments }, false, true);
    return true;
  }

  async function deleteComment(comment: any) {
    let general_comments = value.general_comments.filter(
      ({ author_id, text, date }: any) =>
        author_id !== comment.author_id || text !== comment.text || date !== comment.date,
    );
    await updateDocument({ ...value, general_comments }, false, true);
    return true;
  }

  async function convertToPDF() {
    setUpdatingPdf(true);
    let result = await dispatch(
      api.Documents.convertDocumentToPDF({ documentId: props.document.id }),
      {
        title: 'Convert Document',
        successText: 'Document converted successfully',
      },
    );
    setUpdatingPdf(false);
    if (result) {
      saveDocument(result);
    }
  }

  let approve = isPractitioner && document.approved === false;

  if (physicianId && !physician) return null;

  let disableEdit = props.disableEdit;

  if (document.processing) disableEdit = true;

  function getHTML(html: string) {
    updateDocumentFields({ html });
  }

  const shouldGetHtml = value.repeat_options !== undefined && !value.html;

  return (
    <View
      key={value.id}
      {...props}
      ownPhysician={ownPhysician}
      isAdmin={isAdmin}
      approve={approve}
      physician={physician}
      onPhysicianClick={onPhysicianClick}
      loading={props.loading || loading}
      updatingPdf={updatingPdf}
      value={value}
      linkModal={linkModal}
      setLinkModal={setLinkModal}
      documentTemplateModal={documentTemplateModal}
      patient={patient}
      resetDocument={resetDocument}
      updateDocument={updateDocument}
      updateDocumentFields={updateDocumentFields}
      approveDocument={approveDocument}
      updateDocumentPDF={updateDocumentPDF}
      isPractitioner={isPractitioner}
      hasDocumentChanged={hasDocumentChanged}
      setDocumentTemplateModal={setDocumentTemplateModal}
      handleDateChange={handleDateChange}
      signModal={signModal}
      signModal2={signModal2}
      setSignModal={changeSignModal}
      setSignModal2={changeSignModal2}
      disableEdit={disableEdit}
      showDocumentEdit={showDocumentEdit}
      previewKey={previewKey}
      openDocumentEdit={openDocumentEdit}
      saveDocument={saveDocument}
      isPhlebotomist={isPhlebotomist}
      chargePatient={chargePatient}
      chargingPatient={chargingPatient}
      convertToPDF={convertToPDF}
      pinned={props.pinned}
      allowAdminApprove={allowAdminApprove}
      dispatch={dispatch}
      lockedToId={lockedId}
      lockedByName={lockedName}
      ownId={ownId}
      changePreviewKey={changePreviewKey}
      primaryPhysician={primaryPhysician}
      getHTML={shouldGetHtml ? getHTML : undefined}
      openCPP={openCPP}
      saveComment={saveComment}
      deleteComment={deleteComment}
      submittingComment={false}
    />
  );
};

export default DocumentDetails;
